import Logger from "../../clients/sentry";
import { handleError } from "../../errors";
import { Project } from "../../interfaces/Project";
import { useProject } from "../context/ProjectContext";
import { useBanner } from "../context/BannerContext";
import { clearProjectData } from "../../excel/lifecycle";
import { getProjectReportData } from "../../excel/workbook";
import { PROJECT_UPDATE_ERROR } from "../constants";
import { syncLegacyReportTable, tabulateProjectReportData } from "../../excel/datasources/reportTables";

/**
 * Selecting a project is the workbook's data-invalidation boundary. A hook rather than part of
 * `ProjectSelector`, because the ordering below is what keeps report tables and the React context
 * from disagreeing about which project is loaded, and that reasoning should be findable on its own.
 */
export function useProjectSelection() {
  const { setSelectedProject, setSelectedEstimate, setSelectedGcgr, setIsContractingLoaded, setSelectedCostModel } =
    useProject();
  const { showBanner } = useBanner();

  const selectProject = async (project: Project) => {
    // Project selection is the intentional invalidation boundary for workbook data: caches and
    // report tables are cleared HERE (not at estimate load) because future datasource cards
    // (GCGR-only / contracting-only loads) won't necessarily pass through an estimate load.
    // Re-selecting the project already in the tables is not a boundary crossing — skip the
    // wipe (and keep the loaded-estimate selection intact) so an accidental click doesn't
    // destroy loaded data. getProjectReportData covers legacy Report-table workbooks, and
    // requiring project_name distinguishes a fully tabulated row from the blank base-data
    // fallback row, which SHOULD fall through so it gets repaired by re-tabulating.
    const existingProjectReport = (await getProjectReportData())[0];
    if (existingProjectReport?.project_id === project.id && existingProjectReport.project_name) {
      setSelectedProject(project);
      return;
    }
    setSelectedEstimate(null);
    setSelectedGcgr(null);
    setIsContractingLoaded(false);
    setSelectedCostModel(null);
    try {
      await clearProjectData();
      await tabulateProjectReportData(project.id);
      // clearReportTables above emptied the legacy combined __Report table; repopulate it from the
      // newly switched project's row so old workbook formulas keep resolving. No-op unless the
      // workbook has __Report.
      await syncLegacyReportTable();
      // __GCGR / GCGRReport and ContractingReport are deliberately NOT repopulated. GCGR and
      // contracting are per-project user selections now, so resolving one here would restore
      // hardcoded-first under another name and add a card the user never asked for. __Report is
      // different: its project row is already in hand.
      // Flip React context to the new project only AFTER its .metadata is in place. Setting it earlier
      // leaves a window where selectedProject is the new project but getEstimateReportData still returns
      // the previous project's ids, so charts/exports/staleness could run against the wrong estimate.
      // (selectedProject stays null until here, so the project selector — not the main view — is what
      // renders during the async work.)
      setSelectedProject(project);
    } catch (error) {
      const err = handleError(error, { projectId: project.id, context: "selectProject" });
      // Logged rather than rethrown: the reset below leaves the workbook in a state the user can act
      // on and the banner says so, but a switch that half-cleared the report tables is worth seeing.
      Logger.error("Failed to select project", err);
      // Clears/tabulate failed → report tables may be empty or hold stale ids. Leave no project selected
      // so the user re-selects (and re-tabulates) rather than see missing or wrong data.
      setSelectedProject(null);
      setSelectedEstimate(null);
      setSelectedGcgr(null);
      setIsContractingLoaded(false);
      setSelectedCostModel(null);
      showBanner(PROJECT_UPDATE_ERROR, "error");
    }
  };

  return { selectProject };
}
